// In-page fan-out of "data changed" notices (topics: jobs, stores, production,
// customers, masters, users, finance — see backend/routes/live.py).
//
// Notices come from three places:
//   1. this tab's own writes   — the X-Changed header on API responses (lib/api.js)
//   2. other tabs of this browser — BroadcastChannel
//   3. everyone else            — the server version poll (lib/live.js)
// Own writes also show up in the next poll; `expected` remembers them so the
// poll doesn't announce the same change twice.

const CHANNEL = "erp-live";
const EXPECT_TTL_MS = 30_000;

const listeners = new Set();
const expected = new Map(); // topic -> timestamps of writes the poll hasn't reported yet

let channel = null;
try {
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (e) => {
    const topics = e.data?.topics;
    if (Array.isArray(topics)) {
      expect(topics);
      publish(topics);
    }
  };
} catch {
  // No BroadcastChannel: other tabs catch up through the poll.
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function publish(topics) {
  if (topics.length) listeners.forEach((listener) => listener(topics));
}

function expect(topics) {
  const now = Date.now();
  topics.forEach((t) => expected.set(t, [...(expected.get(t) || []), now]));
}

/** A write from this tab succeeded: tell this tab and the browser's other tabs now. */
export function announceLocalChange(topics) {
  const clean = topics.map((t) => t.trim()).filter(Boolean);
  if (!clean.length) return;
  expect(clean);
  publish(clean);
  channel?.postMessage({ topics: clean });
}

/** Version deltas from the server poll -> topics not already announced locally. */
export function settleServerChanges(deltas) {
  const cutoff = Date.now() - EXPECT_TTL_MS;
  const fresh = [];
  Object.entries(deltas).forEach(([topic, delta]) => {
    const own = (expected.get(topic) || []).filter((at) => at > cutoff);
    // A negative delta means the counters were reset (database restored): always reload.
    const unexplained = delta < 0 ? 1 : delta - own.length;
    expected.set(topic, own.slice(Math.max(delta, 0)));
    if (unexplained > 0) fresh.push(topic);
  });
  return fresh;
}

/** First poll after start-up: everything so far is already on screen. */
export function clearExpected() {
  expected.clear();
}
